import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();
  const { refreshSubscription } = useContext(AuthContext);
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  
  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);
  
  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/payments/checkout/status/${sessionId}`,
        { withCredentials: true }
      );
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        await refreshSubscription();
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }
      
      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };
  
  return (
    <div className="success-page">
      <Card className="success-card" data-testid="success-card">
        <CardHeader>
          <CardTitle>
            {status === "checking" && (
              <div className="status-checking">
                <Loader2 className="animate-spin" size={48} />
                <h2>Processing Payment...</h2>
              </div>
            )}
            {status === "success" && (
              <div className="status-success" data-testid="payment-success">
                <CheckCircle size={48} className="success-icon" />
                <h2>Payment Successful!</h2>
              </div>
            )}
            {status === "expired" && (
              <div className="status-error">
                <h2>Payment Session Expired</h2>
              </div>
            )}
            {status === "timeout" && (
              <div className="status-error">
                <h2>Payment Verification Timeout</h2>
              </div>
            )}
            {status === "error" && (
              <div className="status-error">
                <h2>Error Verifying Payment</h2>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status === "success" && (
            <div className="success-content">
              <p>Your subscription has been activated successfully.</p>
              <p>You can now enjoy all the features of your plan!</p>
              <Button onClick={() => navigate("/dashboard")} data-testid="go-to-dashboard-button">
                Go to Dashboard
              </Button>
            </div>
          )}
          {status !== "success" && status !== "checking" && (
            <div className="error-content">
              <p>Please check your email for confirmation or contact support.</p>
              <Button onClick={() => navigate("/pricing")} data-testid="back-to-pricing-button">
                Back to Pricing
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SuccessPage;
